import { inject, Injectable } from '@angular/core';
import { ApiCallingV2Service, API_CONFIG, AnalyticsEvent, AnalyticsService } from '@zayan-erp/shared';
import { Observable, tap } from 'rxjs';
import {
  AddendumOptionsPayload,
  DealOption,
  DealOptionProduct,
  GenerateAddendumPdfRequest,
  UpdateDealAddendumPayload
} from '../models/deal-options-view.models';
import { AddendumLog } from '../models/deal-options.models';
import { SalesApsService } from './sales-aps.service';

@Injectable({
  providedIn: 'root'
})
export class DealOptionsService {
  private api = inject(ApiCallingV2Service);
  private apiConfig = inject(API_CONFIG);
  baseUrl = this.apiConfig.baseUrlV2;
  #analytics = inject(AnalyticsService);
  #salesAps = inject(SalesApsService);

  getDealOptions(dealId: string): Observable<DealOption[]> {
    return this.api.getData<DealOption[]>(`${this.baseUrl}/deals/${dealId}/addendums?excludeCancelled=true`, {
      showLoader: true,
      showMessage: false,
      showError: false
    });
  }

  getAddendums(lotId: string, dealId: string, showLoader: boolean = true): Observable<DealOption[]> {
    const params = `lotId=${lotId}&dealId=${dealId}`;
    return this.api.getData<DealOption[]>(`${this.baseUrl}/addendums?${params}`, {
      showLoader,
      showMessage: false
    });
  }

  getDealOptionLogs(optionId: string): Observable<AddendumLog[]> {
    return this.api.getData<AddendumLog[]>(`${this.baseUrl}/addendums/${optionId}/logs`, {
      showLoader: true,
      showMessage: false
    });
  }

  deleteOption(optionId: string, ids?: { lotId?: string; dealId?: string }): Observable<void> {
    return this.api
      .deleteData<void>(`${this.baseUrl}/addendums/${optionId}`, {
        showLoader: true,
        showMessage: true,
        message: 'Addendum deleted'
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx(ids);
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.AddendumDeleted, { ...ctx, addendum_id: optionId });
        })
      );
  }

  deleteAddendumProduct(productId: string, ids?: { lotId?: string; dealId?: string }): Observable<void> {
    return this.api
      .deleteData<void>(`${this.baseUrl}/addendum-products/${productId}`, {
        showLoader: true,
        showMessage: true,
        message: 'Product deleted'
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx(ids);
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.AddendumOptionDeleted, {
            ...ctx,
            addendum_option_id: productId
          });
        })
      );
  }

  updateAddendum(addendumId: string, payload: Partial<UpdateDealAddendumPayload>): Observable<void> {
    return this.api.putData<void>(`${this.baseUrl}/addendums/${addendumId}`, payload, {
      showLoader: true,
      buttonId: 'deal-save-addendum-details',
      showMessage: true,
      message: 'Addendum updated'
    });
  }

  updateBuilderReference(addendumId: string, builderReferenceId: string): Observable<void> {
    return this.api.patchData<void>(
      `${this.baseUrl}/addendums/${addendumId}/builder-reference`,
      { builderReferenceId },
      {
        showLoader: true,
        showMessage: true,
        message: 'Builder reference updated'
      }
    );
  }

  toggleApproval(
    addendumId: string,
    payload: { toggleBuyerApproved?: boolean; toggleBuilderApproved?: boolean },
    ids?: { lotId?: string; dealId?: string }
  ): Observable<void> {
    return this.api
      .patchData<void>(`${this.baseUrl}/addendums/${addendumId}/toggle-approval`, payload, {
        showLoader: true,
        buttonId: 'deal-approvals',
        showMessage: true,
        message: 'Approval updated'
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx(ids);
          if (!ctx) return;
          const isBuyer = payload.toggleBuyerApproved !== undefined;
          this.#analytics.capture(AnalyticsEvent.AddendumApprovalToggled, {
            ...ctx,
            addendum_id: addendumId,
            approval_type: isBuyer ? 'buyer' : 'builder',
            approved: isBuyer ? !!payload.toggleBuyerApproved : !!payload.toggleBuilderApproved
          });
        })
      );
  }

  addAddendumOptions(
    addendumId: string,
    payload: AddendumOptionsPayload[],
    ids?: { lotId?: string; dealId?: string }
  ): Observable<void> {
    return this.api
      .postData<void>(
        `${this.baseUrl}/addendums/${addendumId}/products`,
        { showLoader: true, showMessage: false, message: 'Options added' },
        payload
      )
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx(ids);
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.AddendumOptionsAdded, {
            ...ctx,
            addendum_id: addendumId,
            option_count: payload.length
          });
        })
      );
  }

  getAddendumOptionDetailsById(addendumProductId: string): Observable<DealOptionProduct> {
    return this.api.getData<DealOptionProduct>(`${this.baseUrl}/addendum-products/${addendumProductId}`, {
      showLoader: false,
      showMessage: false
    });
  }

  updateAddendumOption(addendumOptionId: string, payload: AddendumOptionsPayload): Observable<void> {
    return this.api.putData<void>(`${this.baseUrl}/addendum-products/${addendumOptionId}`, payload, {
      showLoader: true,
      buttonId: 'deal-save-addendum-option-details',
      showMessage: true,
      message: 'Option updated'
    });
  }

  /** Moves an option on a locked addendum: only its location changes, never its price. */
  /** A null locationId takes the room off the line; the option itself stays. */
  updateAddendumOptionLocation(addendumOptionId: string, locationId: string | null): Observable<void> {
    return this.api.putData<void>(
      `${this.baseUrl}/addendum-products/${addendumOptionId}/location`,
      { locationId },
      {
        showLoader: true,
        showMessage: true,
        message: locationId ? 'Location updated' : 'Location removed'
      }
    );
  }

  updateIncentive(
    addendumOptionId: string,
    payload: { incentiveAmount: number },
    ids?: { lotId?: string; dealId?: string }
  ): Observable<void> {
    return this.api
      .putData<void>(`${this.baseUrl}/addendum-products/${addendumOptionId}/incentive-amount`, payload, {
        showLoader: true,
        buttonId: 'deal-apply-incentive',
        showMessage: true,
        message: 'Incentive updated'
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx(ids);
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.AddendumOptionIncentiveUpdated, {
            ...ctx,
            addendum_option_id: addendumOptionId,
            incentive_value: payload.incentiveAmount
          });
        })
      );
  }

  updateDiscount(
    addendumOptionId: string,
    payload: { discountTypeId: string; discountValue: number },
    ids?: { lotId?: string; dealId?: string }
  ): Observable<void> {
    return this.api
      .putData<void>(`${this.baseUrl}/addendum-products/${addendumOptionId}/discount`, payload, {
        showLoader: true,
        buttonId: 'deal-apply-discount',
        showMessage: true,
        message: 'Discount updated'
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx(ids);
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.AddendumOptionDiscountUpdated, {
            ...ctx,
            addendum_option_id: addendumOptionId,
            discount_type_id: payload.discountTypeId,
            discount_value: payload.discountValue
          });
        })
      );
  }

  updateContractFlags(
    dealId: string,
    payload: { eligibleForGSTRebate: boolean; investmentProperty: boolean }
  ): Observable<void> {
    return this.api.patchData<void>(`${this.baseUrl}/deals/${dealId}/contract/flags`, payload, {
      showLoader: true,
      buttonId: 'deal-update-contract-flags',
      showMessage: true,
      message: 'Contract flags updated'
    });
  }

  getLotAddendums(lotId: string): Observable<DealOption[]> {
    return this.api.getData<DealOption[]>(`${this.baseUrl}/lot/${lotId}/addendums`, {
      showLoader: true,
      showMessage: false,
      showError: false
    });
  }

  cancelAddendum(addendumId: string, ids?: { lotId?: string; dealId?: string }): Observable<void> {
    return this.api
      .postData<void>(`${this.baseUrl}/addendums/${addendumId}/cancel`, {
        showLoader: true,
        showMessage: true,
        message: 'Addendum cancelled'
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx(ids);
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.AddendumCancelled, { ...ctx, addendum_id: addendumId });
        })
      );
  }

  cancelAddendumProduct(addendumProductId: string, ids?: { lotId?: string; dealId?: string }): Observable<void> {
    return this.api
      .postData<void>(`${this.baseUrl}/addendum-products/${addendumProductId}/cancel`, {
        showLoader: true,
        showMessage: true,
        message: 'Addendum product cancelled'
      })
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx(ids);
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.AddendumOptionCancelled, {
            ...ctx,
            addendum_option_id: addendumProductId
          });
        })
      );
  }

  downloadAddendumsPdf(dealId: string, addendumIds: string[] | null, downloadFileName?: string): Observable<void> {
    const body: GenerateAddendumPdfRequest = {
      addendumIds: addendumIds && addendumIds.length > 0 ? addendumIds : null
    };
    return this.api.downloadReportFileWithBody(
      `${this.baseUrl}/deals/${dealId}/addendums/pdf`,
      body,
      { showLoader: true, showMessage: true },
      downloadFileName
    );
  }
}
